import { useEffect, useMemo, useRef, useState } from "react";
import "./Psa10Returns.css";
import { WebsiteHeader } from "./Website";
import {
  PSA10_DATES,
  PSA10_ERA_SERIES,
  PSA10_PERIOD_END,
  PSA10_PERIOD_START,
  SP500_RETURNS,
  type EraId,
  type EraReturnSeries,
} from "./psa10ReturnsData";

const DEFAULT_CHART_WIDTH = 960;
const MAX_CHART_HEIGHT = 560;
const MIN_CHART_HEIGHT = 340;
const CHART_MARGIN = { top: 18, right: 22, bottom: 50, left: 62 };
const COMPACT_CHART_MARGIN = { top: 18, right: 14, bottom: 50, left: 52 };
const LAST_INDEX = PSA10_DATES.length - 1;
const PSA_COLOR = "#368f00";
const SP500_COLOR = "#7c5cff";

type ChartLine = {
  id: string;
  label: string;
  values: readonly number[];
  color: string;
  marker: "circle" | "diamond";
  strokeDasharray?: string;
  strokeWidth: number;
};

const SP500_LINE: ChartLine = {
  id: "sp500",
  label: "S&P 500",
  values: SP500_RETURNS,
  color: SP500_COLOR,
  marker: "diamond",
  strokeDasharray: "4 9",
  strokeWidth: 3.5,
};

const COMBINED_LABELS: Record<EraId, string> = {
  all: "All",
  ultra: "2019+",
  modern: "2014–2019",
  mid: "2007–2013",
  vintage: "1999–2006",
};

const COMBINED_COLORS: Record<EraId, string> = {
  all: PSA_COLOR,
  ultra: "#7c5cff",
  modern: "#f05a38",
  mid: "#008c95",
  vintage: "#d43f8d",
};

const COMBINED_DASHES: Partial<Record<EraId, string>> = {
  ultra: "10 5",
  modern: "2 5",
  mid: "12 5 3 5",
  vintage: "6 3 2 3",
};

const COMBINED_LINES: readonly ChartLine[] = PSA10_ERA_SERIES.map((era) => ({
  id: era.id,
  label: COMBINED_LABELS[era.id],
  values: era.psa10Returns,
  color: COMBINED_COLORS[era.id],
  marker: "circle",
  strokeDasharray: COMBINED_DASHES[era.id],
  strokeWidth: 3.5,
}));

const longDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

const monthFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  timeZone: "UTC",
});

const monthYearFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

function parseIsoDate(isoDate: string) {
  return new Date(`${isoDate}T00:00:00Z`);
}

function formatReturn(value: number, decimals = 2) {
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  return `${sign}${Math.abs(value).toFixed(decimals)}%`;
}

function chartHeightForWidth(width: number) {
  return Math.min(MAX_CHART_HEIGHT, Math.max(MIN_CHART_HEIGHT, width * 0.58));
}

function niceStep(value: number) {
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / (10 ** exponent);
  const niceFraction = fraction <= 1
    ? 1
    : fraction <= 2
      ? 2
      : fraction <= 2.5
        ? 2.5
        : fraction <= 5
          ? 5
          : 10;
  return niceFraction * (10 ** exponent);
}

function buildYScale(lines: readonly ChartLine[]) {
  let dataMin = 0;
  let dataMax = 0;

  for (const line of lines) {
    for (const value of line.values) {
      dataMin = Math.min(dataMin, value);
      dataMax = Math.max(dataMax, value);
    }
  }

  const rawSpan = Math.max(dataMax - dataMin, 1);
  const step = niceStep(rawSpan / 6);
  const min = Math.floor(dataMin / step) * step;
  let max = Math.ceil(dataMax / step) * step;
  if (max === min) max = min + step;

  const ticks: number[] = [];
  for (let tick = min; tick <= max + step / 2; tick += step) {
    ticks.push(Number(tick.toFixed(6)));
  }
  return { min, max, ticks };
}

const X_TICK_INDICES = Array.from(new Set(
  [0, 0.25, 0.5, 0.75, 1].map((position) => Math.round(position * LAST_INDEX)),
));

function xForIndex(
  index: number,
  chartWidth: number,
  margin: typeof CHART_MARGIN,
) {
  const plotWidth = chartWidth - margin.left - margin.right;
  return margin.left + (index / LAST_INDEX) * plotWidth;
}

function yForValue(
  value: number,
  chartHeight: number,
  margin: typeof CHART_MARGIN,
  yScale: ReturnType<typeof buildYScale>,
) {
  const plotHeight = chartHeight - margin.top - margin.bottom;
  const { min, max } = yScale;
  return margin.top + ((max - value) / (max - min)) * plotHeight;
}

function makePath(
  values: readonly number[],
  chartWidth: number,
  chartHeight: number,
  margin: typeof CHART_MARGIN,
  yScale: ReturnType<typeof buildYScale>,
) {
  return values.map((value, index) => {
    const command = index === 0 ? "M" : "L";
    return `${command}${xForIndex(index, chartWidth, margin).toFixed(2)},${yForValue(
      value,
      chartHeight,
      margin,
      yScale,
    ).toFixed(2)}`;
  }).join(" ");
}

type InteractiveReturnsChartProps = {
  chartId: string;
  heading: string;
  subheading: string;
  lines: readonly ChartLine[];
  description: string;
  combined?: boolean;
};

function InteractiveReturnsChart({
  chartId,
  heading,
  subheading,
  lines,
  description,
  combined = false,
}: InteractiveReturnsChartProps) {
  const [activeIndex, setActiveIndex] = useState(LAST_INDEX);
  const [pinnedIndex, setPinnedIndex] = useState<number | null>(null);
  const [announcementIndex, setAnnouncementIndex] = useState<number | null>(null);
  const chartViewportRef = useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize] = useState(() => {
    const initialWidth = typeof window !== "undefined" && window.innerWidth <= 640
      ? Math.max(280, window.innerWidth - 60)
      : DEFAULT_CHART_WIDTH;
    return { width: initialWidth, height: chartHeightForWidth(initialWidth) };
  });
  const activeDate = PSA10_DATES[activeIndex];
  const announcementDate = announcementIndex === null
    ? null
    : PSA10_DATES[announcementIndex];
  const yScale = useMemo(() => buildYScale(lines), [lines]);
  const chart = useMemo(() => {
    const { width, height } = chartSize;
    const margin = width <= 520 ? COMPACT_CHART_MARGIN : CHART_MARGIN;
    const paths = lines.map((line) => ({
      line,
      path: makePath(line.values, width, height, margin, yScale),
    }));
    const x = (index: number) => xForIndex(index, width, margin);
    const y = (value: number) => yForValue(value, height, margin, yScale);

    return { width, height, margin, paths, x, y };
  }, [chartSize, lines, yScale]);

  useEffect(() => {
    const viewport = chartViewportRef.current;
    if (!viewport) return;

    const updateSize = (width: number) => {
      const nextWidth = Math.max(280, width);
      const nextHeight = chartHeightForWidth(nextWidth);
      setChartSize((current) => (
        Math.abs(current.width - nextWidth) < 1 && Math.abs(current.height - nextHeight) < 1
          ? current
          : { width: nextWidth, height: nextHeight }
      ));
    };

    updateSize(viewport.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      updateSize(entries[0].contentRect.width);
    });
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  const indexFromClientX = (clientX: number, chartElement: SVGSVGElement) => {
    const bounds = chartElement.getBoundingClientRect();
    const chartX = ((clientX - bounds.left) / bounds.width) * chart.width;
    const plotWidth = chart.width - chart.margin.left - chart.margin.right;
    const relativeX = Math.min(Math.max(chartX - chart.margin.left, 0), plotWidth);
    return Math.round((relativeX / plotWidth) * LAST_INDEX);
  };

  const showPointAt = (clientX: number, chartElement: SVGSVGElement, pin = false) => {
    const nextIndex = indexFromClientX(clientX, chartElement);
    setActiveIndex(nextIndex);
    if (pin) setPinnedIndex(nextIndex);
    return nextIndex;
  };

  const titleId = `returns-chart-title-${chartId}`;
  const descriptionId = `returns-chart-description-${chartId}`;
  const headingId = `returns-chart-heading-${chartId}`;

  return (
    <figure
      className={`returns-chart-panel${combined ? " returns-chart-panel-combined" : ""}`}
      aria-labelledby={headingId}
    >
      <header className="returns-era-heading">
        <h2 id={headingId}>{heading}</h2>
        <p>{subheading}</p>
      </header>

      <div className={`returns-chart-readout${combined ? " returns-chart-readout-combined" : ""}`}>
        <time dateTime={activeDate}>{longDateFormatter.format(parseIsoDate(activeDate))}</time>
        <div>
          {lines.map((line) => (
            <span key={line.id}>
              {combined ? (
                <svg className="returns-legend-line" viewBox="0 0 24 10" aria-hidden="true">
                  <line
                    x1="1"
                    x2="23"
                    y1="5"
                    y2="5"
                    style={{
                      stroke: line.color,
                      strokeDasharray: line.strokeDasharray,
                      strokeWidth: 3,
                    }}
                  />
                </svg>
              ) : (
                <i
                  className={line.marker === "diamond" ? "returns-legend-diamond" : "returns-legend-dot"}
                  style={{ backgroundColor: line.color }}
                />
              )}
              {line.label} <strong>{formatReturn(line.values[activeIndex])}</strong>
            </span>
          ))}
        </div>
      </div>
      <span className="returns-sr-only" aria-live="polite" aria-atomic="true">
        {announcementDate && announcementIndex !== null
          ? `${longDateFormatter.format(parseIsoDate(announcementDate))}. ${lines
            .map((line) => `${line.label} ${formatReturn(line.values[announcementIndex])}`)
            .join(". ")}.`
          : ""}
      </span>

      <div className="returns-chart-viewport" ref={chartViewportRef}>
        <svg
          className="returns-chart"
          viewBox={`0 0 ${chart.width} ${chart.height}`}
          role="img"
          aria-labelledby={`${titleId} ${descriptionId}`}
          tabIndex={0}
          onPointerDown={(event) => {
            if (event.pointerType === "touch") {
              event.currentTarget.setPointerCapture(event.pointerId);
            }
            showPointAt(event.clientX, event.currentTarget);
          }}
          onPointerMove={(event) => showPointAt(event.clientX, event.currentTarget)}
          onPointerUp={(event) => {
            setAnnouncementIndex(showPointAt(event.clientX, event.currentTarget, true));
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
              event.currentTarget.releasePointerCapture(event.pointerId);
            }
          }}
          onPointerLeave={() => setActiveIndex(pinnedIndex ?? LAST_INDEX)}
          onMouseLeave={() => setActiveIndex(pinnedIndex ?? LAST_INDEX)}
          onPointerCancel={() => setActiveIndex(pinnedIndex ?? LAST_INDEX)}
          onKeyDown={(event) => {
            if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
            event.preventDefault();
            const direction = event.key === "ArrowLeft" ? -1 : 1;
            const nextIndex = Math.min(Math.max(activeIndex + direction, 0), LAST_INDEX);
            setActiveIndex(nextIndex);
            setAnnouncementIndex(nextIndex);
          }}
        >
          <title id={titleId}>{heading}</title>
          <desc id={descriptionId}>
            {description} Daily six-month returns normalized to zero on{" "}
            {longDateFormatter.format(parseIsoDate(PSA10_PERIOD_START))}. Move across the chart for individual values.
          </desc>

          {yScale.ticks.map((tick) => (
            <g key={tick}>
              <line
                className={tick === 0 ? "returns-zero-line" : "returns-grid-line"}
                x1={chart.margin.left}
                x2={chart.width - chart.margin.right}
                y1={chart.y(tick)}
                y2={chart.y(tick)}
              />
              <text
                className="returns-y-label"
                x={chart.margin.left - 14}
                y={chart.y(tick)}
                textAnchor="end"
                dominantBaseline="middle"
              >
                {formatReturn(tick, Number.isInteger(tick) ? 0 : 1)}
              </text>
            </g>
          ))}

          {X_TICK_INDICES.map((index) => (
            <text
              className="returns-x-label"
              key={PSA10_DATES[index]}
              x={chart.x(index)}
              y={chart.height - 13}
              textAnchor={index === 0 ? "start" : index === LAST_INDEX ? "end" : "middle"}
            >
              {monthFormatter.format(parseIsoDate(PSA10_DATES[index]))}
            </text>
          ))}

          {chart.paths.map(({ line, path }) => (
            <path
              className="returns-series"
              d={path}
              key={line.id}
              style={{
                stroke: line.color,
                strokeDasharray: line.strokeDasharray,
                strokeWidth: line.strokeWidth,
              }}
            />
          ))}

          <line
            className="returns-active-line"
            x1={chart.x(activeIndex)}
            x2={chart.x(activeIndex)}
            y1={chart.margin.top}
            y2={chart.height - chart.margin.bottom}
          />
          {lines.map((line) => {
            const activeValue = line.values[activeIndex];
            return line.marker === "diamond" ? (
              <rect
                className="returns-active-dot"
                key={line.id}
                x={chart.x(activeIndex) - 5}
                y={chart.y(activeValue) - 5}
                width="10"
                height="10"
                style={{ fill: line.color }}
                transform={`rotate(45 ${chart.x(activeIndex)} ${chart.y(activeValue)})`}
              />
            ) : (
              <circle
                className="returns-active-dot"
                key={line.id}
                cx={chart.x(activeIndex)}
                cy={chart.y(activeValue)}
                r={combined ? 5 : 6}
                style={{ fill: line.color }}
              />
            );
          })}
        </svg>
      </div>
    </figure>
  );
}

function ReturnsChart({ era }: { era: EraReturnSeries }) {
  const lines = useMemo<readonly ChartLine[]>(() => [
    {
      id: `${era.id}-psa10`,
      label: "PSA 10",
      values: era.psa10Returns,
      color: PSA_COLOR,
      marker: "circle",
      strokeWidth: 4,
    },
    SP500_LINE,
  ], [era]);

  return (
    <InteractiveReturnsChart
      chartId={era.id}
      heading={era.label}
      subheading={`${era.cohortSize.toLocaleString("en-US")} English PSA 10s · ${era.range}`}
      lines={lines}
      description={`${era.label} English PSA 10 index compared with the S&P 500.`}
    />
  );
}

function CombinedReturnsChart() {
  return (
    <InteractiveReturnsChart
      chartId="combined"
      heading="Era comparison"
      subheading="English PSA 10 indexes · one shared scale"
      lines={COMBINED_LINES}
      description="All five English PSA 10 era indexes shown together on one return scale."
      combined
    />
  );
}

export default function Psa10Returns() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "PSA 10 Returns by Era vs. the S&P 500 | Evo";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const lastSp500Return = SP500_RETURNS[LAST_INDEX];

  return (
    <main className="psa10-returns-page">
      <div className="psa10-returns-site-header">
        <WebsiteHeader />
      </div>

      <section className="psa10-returns-hero">
        <h1>PSA 10s vs. the S&amp;P 500</h1>
        <p className="returns-page-subtitle">Six-month returns by Pokémon card era</p>
        <p className="returns-page-period">
          {monthYearFormatter.format(parseIsoDate(PSA10_PERIOD_START))} – {monthYearFormatter.format(parseIsoDate(PSA10_PERIOD_END))}
        </p>
      </section>

      <section className="psa10-returns-chart-section" aria-label="Returns by card era">
        {PSA10_ERA_SERIES.map((era) => <ReturnsChart era={era} key={era.id} />)}
        <CombinedReturnsChart />
      </section>

      <section className="psa10-returns-summary" aria-label="Returns over the entire period">
        {PSA10_ERA_SERIES.map((era) => (
          <article key={era.id}>
            <div>
              <strong>{era.label}</strong>
              <span>{era.cohortSize.toLocaleString("en-US")} cards</span>
            </div>
            <span className="returns-summary-series">
              <i className="returns-legend-dot" />PSA 10 <strong>{formatReturn(era.psa10Returns[LAST_INDEX], 1)}</strong>
            </span>
            <span className="returns-summary-series">
              <i className="returns-legend-diamond" />S&amp;P 500 <strong>{formatReturn(lastSp500Return, 1)}</strong>
            </span>
          </article>
        ))}
      </section>

      <footer className="psa10-returns-footer">
        <a href="/" aria-label="Evo home"><img src="/evo-logo-full.svg" alt="Evo" /></a>
      </footer>
    </main>
  );
}
